import React from 'react';
import { FiHome, FiList, FiMap, FiPackage, FiTruck, FiUsers } from 'react-icons/fi';
import CanvasCoverageMap from './views/CanvasCoverageMap';
import CanvasHosts from './views/CanvasHosts';
import CanvasDrivers from './views/CanvasDrivers';
import CanvasOrders from './views/CanvasOrders';
import CanvasProperties from './views/CanvasProperties';
import { fmtNumber } from './derive';

export const VIEW_DEFS = [
  { id: 'map', label: 'Map', icon: FiMap },
  { id: 'orders', label: 'Orders', icon: FiPackage },
  { id: 'hosts', label: 'Hosts', icon: FiUsers },
  { id: 'properties', label: 'Properties', icon: FiHome },
  { id: 'drivers', label: 'Drivers', icon: FiTruck },
];

export function getViewMeta(view, summary, coverage) {
  const rowCounts = summary?.rowCounts || {};
  switch (view) {
    case 'map':
      return {
        rowsLabel: coverage?.totals ? `${fmtNumber(coverage.totals.drivers)} drivers · ${fmtNumber(coverage.states?.length || 0)} states` : '',
        recordsAvailable: (rowCounts.drivers || 0) > 0,
        recordsView: 'drivers',
      };
    case 'hosts':
      return { rowsLabel: `${fmtNumber(rowCounts.hosts || 0)} hosts in range`, recordsAvailable: (rowCounts.hosts || 0) > 0, recordsView: 'hosts' };
    case 'drivers':
      return { rowsLabel: `${fmtNumber(rowCounts.drivers || 0)} drivers in range`, recordsAvailable: (rowCounts.drivers || 0) > 0, recordsView: 'drivers' };
    case 'orders':
      return { rowsLabel: `${fmtNumber(rowCounts.orders || 0)} orders in range`, recordsAvailable: (rowCounts.orders || 0) > 0, recordsView: 'orders' };
    case 'properties':
      return { rowsLabel: `${fmtNumber(rowCounts.properties || 0)} properties in range`, recordsAvailable: (rowCounts.properties || 0) > 0, recordsView: 'properties' };
    default:
      return { rowsLabel: '', recordsAvailable: false, recordsView: null };
  }
}

export default function DashboardCanvas({
  activeView,
  onChangeView,
  onOpenRecords,
  rangeLabel,
  summary,
  coverage,
  coverageLoading,
  coverageError,
  onRefreshCoverage,
  mapState,
  onChangeMapState,
  focusState,
  onFocusState,
  highlightId,
  onHighlight,
  onSelectHost,
  onSelectPms,
}) {
  const kpis = summary?.kpis || {};
  const leaders = summary?.leaders || {};
  const series = summary?.series || {};
  const breakdowns = summary?.breakdowns || {};
  const meta = getViewMeta(activeView, summary, coverage);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-3 py-2">
        <div className="flex items-center gap-1 overflow-x-auto" role="tablist" aria-label="Dashboard view">
          {VIEW_DEFS.map((viewDef) => {
            const Icon = viewDef.icon;
            const active = activeView === viewDef.id;
            return (
              <button
                key={viewDef.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onChangeView?.(viewDef.id)}
                className={`group inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[11px] font-medium transition-colors duration-150 motion-reduce:transition-none ${
                  active ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${active ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-700'}`} />
                {viewDef.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] text-zinc-500">{meta.rowsLabel}</span>
          <button
            type="button"
            onClick={() => meta.recordsAvailable && onOpenRecords?.(meta.recordsView)}
            disabled={!meta.recordsAvailable}
            title={meta.recordsAvailable ? 'Open records sheet' : 'No records to view'}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-[11px] font-medium text-zinc-700 transition-colors duration-150 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
          >
            <FiList className="h-3 w-3" />
            Records
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden p-3">
        <div key={activeView} className="h-full animate-[fadeInUp_180ms_ease-out_both] motion-reduce:animate-none">
          {activeView === 'map' ? (
            <CanvasCoverageMap
              coverage={coverage}
              loading={coverageLoading}
              error={coverageError}
              metric={mapState.metric}
              onChangeMetric={(metric) => onChangeMapState({ metric })}
              layer={mapState.layer}
              onChangeLayer={(layer) => onChangeMapState({ layer })}
              focusState={focusState}
              onFocusState={onFocusState}
              highlightId={highlightId}
              onHighlight={onHighlight}
              onRefresh={onRefreshCoverage}
              refreshing={coverageLoading}
            />
          ) : activeView === 'orders' ? (
            <CanvasOrders series={series.orders || []} total={kpis.orders || 0} revenue={kpis.revenue || 0} aov={kpis.aov || 0} rangeLabel={rangeLabel} />
          ) : activeView === 'hosts' ? (
            <CanvasHosts
              series={series.hosts || []}
              total={kpis.hosts || 0}
              rows={leaders.topHosts || []}
              rangeLabel={rangeLabel}
              onSelectHost={onSelectHost}
            />
          ) : activeView === 'properties' ? (
            <CanvasProperties
              series={series.properties || []}
              rows={(breakdowns.propertyPms || []).map((row) => ({ id: row._id, count: row.count }))}
              total={kpis.properties || 0}
              rangeLabel={rangeLabel}
              onSelectPms={onSelectPms}
            />
          ) : (
            <CanvasDrivers
              series={series.drivers || []}
              totalInRange={kpis.drivers || 0}
              activeDrivers={summary?.platform?.activeDrivers || 0}
              totalDrivers={summary?.platform?.totalDrivers || 0}
              rangeLabel={rangeLabel}
              coverage={coverage}
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
